import { clsx } from 'clsx'
import { useRouter } from 'next/navigation'
import { type FC, type JSX, type ReactNode, useEffect, useState } from 'react'
import { FormProvider, useForm, type UseFormProps } from 'react-hook-form'
import { FormattedMessage, useIntl } from 'react-intl'
import isEmail from 'validator/lib/isEmail'

import { type RXPrescriptionRequestV1TitleEnum } from '@redteclab/api/clients/bully'
import { IconInfo } from '@redteclab/icons'

import { Button, Input, Link, Tooltip } from 'base-ui'

import {
  RadioInputGroupHookFormController,
  type RadioParams,
  useRunResetOnSubmitSuccessfull,
} from '../../../../../base-form-handling'
import {
  FriendlyCaptchaChallenge,
  useHookFormFriendlyCaptchaFieldControls,
} from '../../../../../friendly-captcha'
import { useGlobalConfigContext } from '../../../../../global-config'
import {
  type FormSubmissionStatus,
  MediServiceFormSubmissionNotification,
  rxGetCampaignCode,
} from '../../../../../medi-service'
import {
  urlResolverGetMediServiceRxLandingPagePrivacyPolicy,
  urlResolverGetRedeemPaperPrescriptionConfirmation,
} from '../../../../../url-handling'
import { useApiSwrRxLandingPageContactControllerPrescriptionPostMutation } from '../../api/useApiSwrRxLandingPageContactControllerPrescriptionPostMutation'
import { usePrefillRxFormUserData } from '../../hooks/usePrefillRxFormUserData'
import { userDateToIsoDate } from '../../processors/userDatetoIsoDate'
import { type RxLpChMainFormFields } from '../../types'
import { rxLpChIsValidDate } from '../../validators/rxLpChIsValidDate'
import { rxLpChIsValidPhoneNumber } from '../../validators/rxLpChIsValidPhoneNumber'
import { RxLpChCheckbox } from '../Checkbox/RxLpChCheckbox'
import { RxLpChCheckboxGroupController } from '../Checkbox/RxLpChCheckboxGroupController'
import { type CheckboxParams } from '../Checkbox/RxLpChCheckboxGroupFieldset'
import { RxLpChErrorMessage } from '../RxLpChErrorMessage'

import styles from './MainForm.module.css'

/* eslint-disable max-statements -- the rule was disabled before a comment became required */
const onSubmit = async (
  data: RxLpChMainFormFields,
  submitMutation: ReturnType<
    typeof useApiSwrRxLandingPageContactControllerPrescriptionPostMutation
  >,
  setFormSubmissionStatus: (status: FormSubmissionStatus) => void,
): Promise<void> => {
  if (submitMutation.isMutating) {
    return
  }

  const newData = { ...data }

  delete newData.deliveryAddressSwitch
  delete newData.toggleHealthQuestionnaireSwitch

  if (newData.medicalConditions) {
    newData.medicalConditions = newData.medicalConditions.filter(Boolean)
  }

  if (newData.otherConditionsCheck && newData.otherConditions) {
    newData.medicalConditions ??= []

    newData.medicalConditions.push(newData.otherConditions)

    delete newData.otherConditions
  }

  delete newData.otherConditionsCheck

  if (newData.pregnant) {
    newData.pregnancy = newData.pregnant === 'true'
    delete newData.pregnant
  }

  if (newData.breastfeeding) {
    newData.breastfeed = newData.breastfeeding === 'true'
    delete newData.breastfeeding
  }

  if (newData.weight) {
    newData.weight = Number(newData.weight)
  }

  if (newData.height) {
    newData.height = Number(newData.height)
  }

  try {
    if (newData.birthdate) {
      newData.birthdate = userDateToIsoDate(newData.birthdate)
    }

    if (newData.birthdateChildren) {
      newData.birthdateChildren = userDateToIsoDate(newData.birthdateChildren)
    }

    await submitMutation.trigger(newData)
    setFormSubmissionStatus('success')
  } catch {
    setFormSubmissionStatus('error')
  }
}
/* eslint-enable max-statements */

const setUndefinedIfEmptyString = (value: string): string | undefined =>
  value === '' ? undefined : value

const setUndefinedIfEmptyNumber = (
  value: string | null,
): string | null | undefined =>
  Number.isNaN(Number(value)) || Number(value) === 0 ? undefined : value

/* Explanation can be found here app/domain/medi-service/components/Faq/Faq.tsx */
/* no-literals is for asterisks in the input labels */
/* eslint-disable @typescript-eslint/no-misused-promises, max-lines, max-lines-per-function, react/jsx-no-literals, react/jsx-max-depth, max-statements -- the rule was disabled before a comment became required */
export const RxLpChMainForm: FC = () => {
  const formSubmitMutation =
    useApiSwrRxLandingPageContactControllerPrescriptionPostMutation()
  const [formSubmissionStatus, setFormSubmissionStatus] =
    useState<FormSubmissionStatus>(false)

  const intl = useIntl()
  const config = useGlobalConfigContext()
  const router = useRouter()

  const link = (text: ReactNode[]): JSX.Element => (
    <Link
      href={urlResolverGetMediServiceRxLandingPagePrivacyPolicy(config)}
      rel="noreferrer"
      target="_blank"
    >
      {text}
    </Link>
  )
  useEffect(() => {
    if (formSubmissionStatus === 'success') {
      router.push(urlResolverGetRedeemPaperPrescriptionConfirmation(config))
    }
  }, [config, formSubmissionStatus, router])

  const formParams: UseFormProps<RxLpChMainFormFields> = {
    defaultValues: {
      deliveryAddressSwitch: 'same',
      title:
        'chrx.questionnaire.pronouns.mrs' as RXPrescriptionRequestV1TitleEnum,
    },
    mode: 'onBlur',
    shouldUnregister: true,
  }
  const formMethods = useForm<RxLpChMainFormFields>(formParams)
  const {
    control,
    formState,
    handleSubmit,
    register,
    reset,
    setValue,
    unregister,
    watch,
  } = formMethods

  usePrefillRxFormUserData(setValue)

  useEffect(() => {
    const campaignCodeRx = rxGetCampaignCode()
    if (campaignCodeRx) {
      setValue('campaignCode', campaignCodeRx)
    }
  }, [setValue])

  useRunResetOnSubmitSuccessfull(formState.isSubmitSuccessful, reset)
  const { onCaptchaDone, watchFriendlyCaptchaChange } =
    useHookFormFriendlyCaptchaFieldControls<RxLpChMainFormFields>({
      fieldName: 'fcSolution',
      register,
      setValue,
      watch,
    })

  const watchDeliveryAddressSwitch = watch('deliveryAddressSwitch')
  const watchToggleHealthQuestionnaireSwitch = watch(
    'toggleHealthQuestionnaireSwitch',
  )

  useEffect(() => {
    if (watchDeliveryAddressSwitch === 'same') {
      unregister('deliveryAddress')
    }
  }, [unregister, watchDeliveryAddressSwitch])

  const titleRadios: RadioParams[] = [
    {
      dataQaId: 'chrx.questionnaire.pronouns.mrs',
      id: 'mrs',
      label: `${intl.formatMessage({
        id: 'chrx.questionnaire.pronouns.mrs',
      })}*`,
      value: 'chrx.questionnaire.pronouns.mrs',
    },
    {
      dataQaId: 'chrx.questionnaire.pronouns.mr',
      id: 'mr',
      label: `${intl.formatMessage({ id: 'chrx.questionnaire.pronouns.mr' })}*`,
      value: 'chrx.questionnaire.pronouns.mr',
    },
  ]

  const deliveryAddressRadios: RadioParams[] = [
    {
      dataQaId: 'chrx.questionnaire.delivery-address.same',
      id: 'sameAddress',
      label: intl.formatMessage({
        id: 'chrx.questionnaire.delivery-address.same',
      }),
      value: 'same',
    },
    {
      dataQaId: 'chrx.questionnaire.delivery-address.different',
      id: 'differentAddress',
      label: intl.formatMessage({
        id: 'chrx.questionnaire.delivery-address.different',
      }),
      value: 'different',
    },
  ]

  const pregnancyRadios: RadioParams[] = [
    {
      dataQaId: 'chrx.pregnant.yes',
      id: 'yes_pregnant',
      label: intl.formatMessage({
        id: 'chrx.yes',
      }),
      value: 'true',
    },
    {
      dataQaId: 'chrx.pregnant.no',
      id: 'no_pregnant',
      label: intl.formatMessage({
        id: 'chrx.no',
      }),
      value: 'false',
    },
  ]

  const breastfeedingRadios: RadioParams[] = [
    {
      dataQaId: 'chrx.breastfeeding.yes',
      id: 'yes_breastfeeding',
      label: intl.formatMessage({
        id: 'chrx.yes',
      }),
      value: 'true',
    },
    {
      dataQaId: 'chrx.breastfeeding.no',
      id: 'no_breastfeeding',
      label: intl.formatMessage({
        id: 'chrx.no',
      }),
      value: 'false',
    },
  ]

  const medicalConditionsCheckboxes: CheckboxParams[] = [
    {
      id: 'diabetes',
      label: intl.formatMessage({
        id: 'chrx.questionnaire.health-info.medical-conditions-list.conditions.diabetes',
      }),
      name: 'diabetes',
      value:
        'chrx.questionnaire.health-info.medical-conditions-list.conditions.diabetes',
    },
    {
      id: 'hypertension',
      label: intl.formatMessage({
        id: 'chrx.questionnaire.health-info.medical-conditions-list.conditions.hypertension',
      }),
      name: 'hypertension',
      value:
        'chrx.questionnaire.health-info.medical-conditions-list.conditions.hypertension',
    },
    {
      id: 'bronchialAsthma',
      label: intl.formatMessage({
        id: 'chrx.questionnaire.health-info.medical-conditions-list.conditions.bronchial-asthma',
      }),
      name: 'bronchialAsthma',
      value:
        'chrx.questionnaire.health-info.medical-conditions-list.conditions.bronchial-asthma',
    },
    {
      id: 'bloodClottingDisorder',
      label: intl.formatMessage({
        id: 'chrx.questionnaire.health-info.medical-conditions-list.conditions.blood-clotting-disorder',
      }),
      name: 'bloodClottingDisorder',
      value:
        'chrx.questionnaire.health-info.medical-conditions-list.conditions.blood-clotting-disorder',
    },
    {
      id: 'cardioVascularDiseases',
      label: intl.formatMessage({
        id: 'chrx.questionnaire.health-info.medical-conditions-list.conditions.cardio-vascular-diseases',
      }),
      name: 'cardioVascularDiseases',
      value:
        'chrx.questionnaire.health-info.medical-conditions-list.conditions.cardio-vascular-diseases',
    },
    {
      id: 'liverProblems',
      label: intl.formatMessage({
        id: 'chrx.questionnaire.health-info.medical-conditions-list.conditions.liver-diseases',
      }),
      name: 'liverProblems',
      value:
        'chrx.questionnaire.health-info.medical-conditions-list.conditions.liver-diseases',
    },
    {
      id: 'kidneyDiseases',
      label: intl.formatMessage({
        id: 'chrx.questionnaire.health-info.medical-conditions-list.conditions.kidney-disease',
      }),
      name: 'kidneyDiseases',
      value:
        'chrx.questionnaire.health-info.medical-conditions-list.conditions.kidney-disease',
    },
  ]

  return (
    <div className="bg-light-primary-medium tablet:flex tablet:justify-center">
      <FormProvider {...formMethods}>
        <form
          id="rx-form"
          noValidate
          onSubmit={handleSubmit(async (data) => {
            await onSubmit(data, formSubmitMutation, setFormSubmissionStatus)
          })}
        >
          <div
            className={clsx(
              styles['main-form-container'],
              'container gap-6 px-4 py-6 tablet:gap-12 tablet:p-0 tablet:py-12',
            )}
          >
            <div
              className={clsx(
                styles['prescription-form'],
                'bg-light-primary-low p-4 tablet:w-[600px] tablet:p-6',
              )}
            >
              <h2
                className={styles['form-heading']}
                data-qa-id="rx-questionaire-title"
              >
                <FormattedMessage id="chrx.questionnaire.title" />
              </h2>
              <RadioInputGroupHookFormController
                className={clsx(
                  styles.titles,
                  'flex flex-wrap tablet:flex-nowrap',
                )}
                containerClassName="flex flex-col items-start gap-2.5"
                /* @ts-expect-error need more generic type here */
                control={control}
                name="title"
                radios={titleRadios}
                rules={{
                  required: intl.formatMessage({
                    id: 'chrx.messages.validation.required',
                  }),
                }}
              />
              <div className={styles['prescription-info-form']}>
                <div className="row flex flex-col items-start gap-4 self-stretch tablet:flex-row tablet:gap-2">
                  <div className="flex flex-1 flex-col items-start gap-2.5 self-stretch tablet:self-auto">
                    <Input
                      {...register('firstname', {
                        required: intl.formatMessage({
                          id: 'chrx.messages.validation.required',
                        }),
                      })}
                      className="flex-1 self-stretch"
                      data-qa-id="chrx.questionnaire.firstname-placeholder"
                      id="firstname"
                      name="firstname"
                      placeholder={`${intl.formatMessage({
                        id: 'chrx.questionnaire.firstname-placeholder',
                      })}*`}
                    />
                    <RxLpChErrorMessage
                      dataQaId="chrx-main-form-firstname-error"
                      name="firstname"
                    />
                  </div>
                  <div className="flex flex-1 flex-col items-start gap-2.5 self-stretch tablet:self-auto">
                    <Input
                      {...register('lastname', {
                        required: intl.formatMessage({
                          id: 'chrx.messages.validation.required',
                        }),
                      })}
                      className="flex-1 self-stretch"
                      data-qa-id="chrx.questionnaire.lastname-placeholder"
                      id="lastname"
                      name="lastname"
                      placeholder={`${intl.formatMessage({
                        id: 'chrx.questionnaire.lastname-placeholder',
                      })}*`}
                    />
                    <RxLpChErrorMessage
                      dataQaId="chrx-main-form-lastname-error"
                      name="lastname"
                    />
                  </div>
                </div>
                <div className="row flex flex-col items-start gap-4 self-stretch tablet:flex-row tablet:gap-2">
                  <div className="flex flex-1 flex-col items-start gap-2.5 self-stretch tablet:self-auto">
                    <Input
                      {...register('street', {
                        required: intl.formatMessage({
                          id: 'chrx.messages.validation.required',
                        }),
                      })}
                      className="flex-1 self-stretch"
                      data-qa-id="chrx.questionnaire.street-placeholder"
                      id="street"
                      name="street"
                      placeholder={`${intl.formatMessage({
                        id: 'chrx.questionnaire.street-placeholder',
                      })}*`}
                    />
                    <RxLpChErrorMessage
                      dataQaId="chrx-main-form-street-error"
                      name="street"
                    />
                  </div>
                  <div className="flex flex-col items-start gap-2.5 self-stretch tablet:w-[159px] tablet:flex-none tablet:self-auto">
                    <Input
                      {...register('streetNo', {
                        required: intl.formatMessage({
                          id: 'chrx.messages.validation.required',
                        }),
                      })}
                      className="w-full flex-[1-0-0] tablet:w-[159px] tablet:flex-none"
                      data-qa-id="chrx.questionnaire.house-number-placeholder"
                      id="streetNo"
                      name="streetNo"
                      placeholder={`${intl.formatMessage({
                        id: 'chrx.questionnaire.house-number-placeholder',
                      })}*`}
                    />
                    <RxLpChErrorMessage
                      dataQaId="chrx-main-form-street-number-error"
                      name="streetNo"
                    />
                  </div>
                </div>
                <div className="row flex flex-col items-start gap-4 self-stretch tablet:flex-row tablet:gap-2">
                  <div className="flex flex-1 flex-col items-start gap-2.5 self-stretch tablet:self-auto">
                    <Input
                      {...register('zip', {
                        required: intl.formatMessage({
                          id: 'chrx.messages.validation.required',
                        }),
                      })}
                      className="flex-1 self-stretch"
                      data-qa-id="chrx.questionnaire.zip-placeholder"
                      id="zip"
                      name="zip"
                      placeholder={`${intl.formatMessage({
                        id: 'chrx.questionnaire.zip-placeholder',
                      })}*`}
                    />
                    <RxLpChErrorMessage
                      dataQaId="chrx-main-form-zip-error"
                      name="zip"
                    />
                  </div>
                  <div className="flex flex-1 flex-col items-start gap-2.5 self-stretch tablet:self-auto">
                    <Input
                      {...register('city', {
                        required: intl.formatMessage({
                          id: 'chrx.messages.validation.required',
                        }),
                      })}
                      className="flex-1 self-stretch"
                      data-qa-id="chrx.questionnaire.city-placeholder"
                      id="city"
                      name="city"
                      placeholder={`${intl.formatMessage({
                        id: 'chrx.questionnaire.city-placeholder',
                      })}*`}
                    />
                    <RxLpChErrorMessage
                      dataQaId="chrx-main-form-city-error"
                      name="city"
                    />
                  </div>
                </div>
                <div className="row flex items-start self-stretch">
                  <div className="flex flex-1 flex-col items-start gap-2.5">
                    <Input
                      {...register('phone', {
                        required: intl.formatMessage({
                          id: 'chrx.messages.validation.required',
                        }),
                        validate: (input) =>
                          rxLpChIsValidPhoneNumber(input) ||
                          intl.formatMessage({
                            id: 'chrx.messages.validation.phone',
                          }),
                      })}
                      className="flex-1 self-stretch"
                      data-qa-id="chrx.questionnaire.phone-number-placeholder"
                      id="phone"
                      name="phone"
                      placeholder={`${intl.formatMessage({
                        id: 'chrx.questionnaire.phone-number-placeholder',
                      })}*`}
                    />
                    <RxLpChErrorMessage
                      dataQaId="chrx-main-form-phone-error"
                      name="phone"
                    />
                  </div>
                </div>
                <div className="row flex items-start self-stretch">
                  <div className="flex flex-1 flex-col items-start gap-2.5">
                    <Input
                      {...register('email', {
                        required: intl.formatMessage({
                          id: 'chrx.messages.validation.required',
                        }),
                        validate: (input) =>
                          isEmail(input) ||
                          intl.formatMessage(
                            {
                              id: 'chrx.messages.validation.email',
                            },
                            { emailValue: input },
                          ),
                      })}
                      className="flex-1 self-stretch"
                      data-qa-id="chrx.questionnaire.email-placeholder"
                      id="email"
                      name="email"
                      placeholder={`${intl.formatMessage({
                        id: 'chrx.questionnaire.email-placeholder',
                      })}*`}
                      type="email"
                    />
                    <RxLpChErrorMessage
                      dataQaId="chrx-main-form-email-error"
                      name="email"
                    />
                  </div>
                </div>
                <div className="row flex items-start self-stretch">
                  <div className="flex flex-1 flex-col items-start gap-2.5">
                    <Input
                      {...register('birthdate', {
                        required: intl.formatMessage({
                          id: 'chrx.messages.validation.required',
                        }),
                        validate: (input) =>
                          rxLpChIsValidDate(input) ||
                          intl.formatMessage({
                            id: 'chrx.messages.validation.date',
                          }),
                      })}
                      className="flex-1 self-stretch"
                      data-qa-id="chrx.questionnaire.birthdate-placeholder"
                      id="birthdate"
                      name="birthdate"
                      placeholder={`${intl.formatMessage({
                        id: 'chrx.questionnaire.birthdate-placeholder',
                      })}*`}
                    />
                    <RxLpChErrorMessage
                      dataQaId="chrx-main-form-birthdate-error"
                      name="birthdate"
                    />
                  </div>
                </div>
                <div className="row flex items-start self-stretch">
                  <div className="flex flex-1 flex-col items-start gap-2.5">
                    <Input
                      {...register('insuranceNumber')}
                      className="flex-1 self-stretch"
                      data-qa-id="chrx.questionnaire.insurance-number-placeholder"
                      id="insuranceNumber"
                      name="insuranceNumber"
                      placeholder={intl.formatMessage({
                        id: 'chrx.questionnaire.insurance-number-placeholder',
                      })}
                    />
                    <RxLpChErrorMessage
                      dataQaId="chrx-main-form-insurance-number-error"
                      name="insuranceNumber"
                    />
                  </div>
                </div>
                <div className={clsx(styles['delivery-address'], 'w-full')}>
                  <h3 className="font-bold">
                    <FormattedMessage id="chrx.questionnaire.delivery-address" />
                    *
                  </h3>
                  <RadioInputGroupHookFormController
                    className="flex flex-col items-start gap-3"
                    /* @ts-expect-error need more generic type here */
                    control={control}
                    name="deliveryAddressSwitch"
                    radios={deliveryAddressRadios}
                  />
                  {watchDeliveryAddressSwitch === 'different' && (
                    <div className="row flex self-stretch pl-[35px]">
                      <div className="flex flex-1 flex-col items-start gap-2.5">
                        <Input
                          {...register('deliveryAddress', {
                            required: intl.formatMessage({
                              id: 'chrx.messages.validation.required',
                            }),
                          })}
                          className={clsx(styles['textarea-custom'], 'flex-1')}
                          id="deliveryAddress"
                          key="deliveryAddress"
                          multiline
                          placeholder={intl.formatMessage({
                            id: 'chrx.questionnaire.delivery-address.different',
                          })}
                          slotProps={{
                            input: { className: 'self-stretch min-h-[120px]' },
                          }}
                        />
                        <RxLpChErrorMessage
                          dataQaId="chrx-main-form-insurance-number-error"
                          name="deliveryAddress"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <span className="h-px w-full bg-light-primary-high content-none" />
              <div>
                <RxLpChCheckbox
                  {...register('toggleHealthQuestionnaireSwitch')}
                  id="toggleHealthQuestionnaireSwitch"
                  label={intl.formatMessage({
                    id: 'chrx.questionnaire.proceed.health-info',
                  })}
                  name="toggleHealthQuestionnaireSwitch"
                />
              </div>
              {watchToggleHealthQuestionnaireSwitch ? (
                <div className="flex flex-col items-start gap-6 self-stretch p-0">
                  {/* check heading sizes */}
                  <div className={styles['health-questionnaire-title']}>
                    <h2 className={styles['form-heading']}>
                      <FormattedMessage id="chrx.questionnaire.health-info.title" />
                    </h2>
                    <span>
                      <FormattedMessage id="chrx.questionnaire.health-info.description" />
                    </span>
                    <div className="row flex flex-col items-start gap-4 self-stretch tablet:flex-row tablet:gap-2">
                      <Input
                        {...register('height', {
                          setValueAs: setUndefinedIfEmptyNumber,
                          validate: (input) =>
                            Boolean(!input) || !Number.isNaN(Number(input)),
                        })}
                        className="flex-1 self-stretch"
                        id="height"
                        key="height"
                        name="height"
                        placeholder={intl.formatMessage({
                          id: 'chrx.questionnaire.health-info.height-placeholder',
                        })}
                      />
                      <Input
                        {...register('weight', {
                          setValueAs: setUndefinedIfEmptyNumber,
                          validate: (input) =>
                            Boolean(!input) || !Number.isNaN(Number(input)),
                        })}
                        className="flex-1 self-stretch"
                        id="weight"
                        key="weight"
                        name="weight"
                        placeholder={intl.formatMessage({
                          id: 'chrx.questionnaire.health-info.weight-placeholder',
                        })}
                      />
                    </div>
                  </div>
                  <div className={styles['health-questionnaire-form']}>
                    <div className={styles['health-questionnaire-section']}>
                      <h3 className="font-bold">
                        <FormattedMessage id="chrx.questionnaire.health-info.medical-conditions-list.title" />
                      </h3>
                      <div className={styles['checkbox-group']}>
                        <RxLpChCheckboxGroupController
                          checkboxes={medicalConditionsCheckboxes}
                          className={styles['checkbox-group']}
                          /* @ts-expect-error need more generic type here */
                          control={control}
                          name="medicalConditions"
                        />
                        <RxLpChCheckbox
                          {...register('otherConditionsCheck')}
                          id="otherConditionsCheck"
                          label={intl.formatMessage({
                            id: 'chrx.questionnaire.health-info.medical-conditions-list.conditions.other',
                          })}
                          name="otherConditionsCheck"
                        />
                        <Input
                          {...register('otherConditions', {
                            setValueAs: setUndefinedIfEmptyString,
                          })}
                          className="flex-1 self-stretch"
                          id="otherConditions"
                          key="otherConditions"
                          name="otherConditions"
                          placeholder={intl.formatMessage({
                            id: 'chrx.questionnaire.health-info.medical-conditions-list.other-conditions-placeholder',
                          })}
                        />
                      </div>
                    </div>
                    <div className={styles['health-questionnaire-section']}>
                      <h3 className="font-bold">
                        <FormattedMessage id="chrx.questionnaire.health-info.allergies.title" />
                      </h3>
                      <div className="row flex self-stretch">
                        <Input
                          {...register('allergies', {
                            setValueAs: setUndefinedIfEmptyString,
                          })}
                          className={clsx(styles['textarea-custom'], 'flex-1')}
                          id="allergies"
                          key="allergies"
                          multiline
                          placeholder={intl.formatMessage({
                            id: 'chrx.questionnaire.health-info.allergies.text-placeholder',
                          })}
                          slotProps={{
                            input: { className: 'self-stretch min-h-[120px]' },
                          }}
                        />
                      </div>
                    </div>
                    <div className={styles['health-questionnaire-section']}>
                      <div className="flex items-center">
                        <h3 className="font-bold">
                          <FormattedMessage id="chrx.questionnaire.health-info.medications.title" />
                          <Tooltip
                            className="w-10/12 bg-light-primary-medium text-s text-dark-primary-max tablet:w-3/12"
                            placement="top"
                            title={
                              <FormattedMessage id="chrx.questionnaire.health-info.medications.tooltip" />
                            }
                          >
                            <IconInfo className="ml-2 inline size-4 text-dark-accent" />
                          </Tooltip>
                        </h3>
                      </div>
                      <div className="row flex self-stretch">
                        <Input
                          {...register('medication', {
                            setValueAs: setUndefinedIfEmptyNumber,
                          })}
                          className={clsx(styles['textarea-custom'], 'flex-1')}
                          id="medication"
                          key="medication"
                          multiline
                          placeholder={intl.formatMessage({
                            id: 'chrx.questionnaire.health-info.medications.text-placeholder',
                          })}
                          slotProps={{
                            input: { className: 'self-stretch min-h-[120px]' },
                          }}
                        />
                      </div>
                    </div>
                    <div className={styles['health-questionnaire-section']}>
                      <h3 className="font-bold">
                        <FormattedMessage id="chrx.questionnaire.health-info.pregnancy.title" />
                      </h3>
                      <RadioInputGroupHookFormController
                        className={styles['radios__horizontal-wider']}
                        /* @ts-expect-error need more generic type here */
                        control={control}
                        name="pregnant"
                        radios={pregnancyRadios}
                      />
                    </div>
                    <div className={styles['health-questionnaire-section']}>
                      <h3 className="font-bold">
                        <FormattedMessage id="chrx.questionnaire.health-info.pregnancy.expected-birthdate.title" />
                      </h3>
                      <div className="row flex self-stretch">
                        <div className="flex flex-1 flex-col items-start gap-2.5">
                          <Input
                            {...register('birthdateChildren', {
                              setValueAs: setUndefinedIfEmptyString,
                              validate: (input) =>
                                Boolean(!input) ||
                                rxLpChIsValidDate(input) ||
                                intl.formatMessage({
                                  id: 'chrx.messages.validation.date',
                                }),
                            })}
                            className="flex-1 self-stretch"
                            id="birthdateChildren"
                            key="birthdateChildren"
                            name="birthdateChildren"
                            placeholder={intl.formatMessage({
                              id: 'chrx.questionnaire.health-info.pregnancy.expected-birthdate.text-placeholder',
                            })}
                          />
                          <RxLpChErrorMessage
                            dataQaId="chrx-main-form-birthdate-children-error"
                            name="birthdateChildren"
                          />
                        </div>
                      </div>
                    </div>
                    <div className={styles['health-questionnaire-section']}>
                      <h3 className="font-bold">
                        <FormattedMessage id="chrx.questionnaire.health-info.pregnancy.breastfeeding.title" />
                      </h3>
                      <RadioInputGroupHookFormController
                        className={styles['radios__horizontal-wider']}
                        /* @ts-expect-error need more generic type here */
                        control={control}
                        name="breastfeeding"
                        radios={breastfeedingRadios}
                      />
                    </div>
                  </div>
                </div>
              ) : null}
              <div className={styles['data-processing-warning']}>
                <FormattedMessage
                  id="chrx.questionnaire.data-processing-warning"
                  values={{ link }}
                />
              </div>
              <FriendlyCaptchaChallenge
                className="mb-3.5"
                doneCallback={onCaptchaDone}
                startMode="auto"
                whenThisPropChangesResetCaptcha={
                  formSubmitMutation.data ?? formSubmitMutation.error
                }
              />
              <Button
                className="w-full"
                data-qa-id="chrx.questionnaire.submit"
                disabled={!watchFriendlyCaptchaChange}
                type="submit"
              >
                <FormattedMessage id="chrx.questionnaire.submit" />
              </Button>
              {formSubmissionStatus !== 'success' && (
                <MediServiceFormSubmissionNotification
                  dataQaId={`chrx-main-form-submission-${formSubmissionStatus}`}
                  formSubmissionStatus={formSubmissionStatus}
                />
              )}
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  )
}
/* eslint-enable @typescript-eslint/no-misused-promises, max-lines, max-lines-per-function, react/jsx-no-literals, react/jsx-max-depth, max-statements */
